import React, { useState } from 'react';
import {
  DashboardWrapper,
  DashboardContainer,
  HeaderSection,
  SectionTitle,
  NewApplicationButton,
  ApplicationsGrid,
  ApplicationCard,
  ApplicationIcon,
  ApplicationHeader,
  ApplicationTitle,
  ApplicationMeta,
  ApplicationActions,
  ViewButton,
  EmptyState,
  SectionHeader,
} from './dashboard.styles';
import { File, ExternalLink, Hash, PlusIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAllChatBots } from '../../../services/general';
import CreateAndUpdateCouncilAgent from './modals/CreateAndUpdateCouncilAgent';

const Dashboard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [modalState, setModalState] = useState({
    isOpen: false,
    isUpdate: false,
    agentData: null,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['chatbotsList'],
    queryFn: () => getAllChatBots(),
    placeholderData: (previousData) => previousData,
  });

  const applications = Array.isArray(data) ? data : (data?.chatbotList ?? []);

  const handleView = (app) => {
    const identifier = app._id || app.id;
    navigate(`/invoices/${identifier}`);
  };

  const handleInitiateChatBot = (agentCode) => {
    if (!agentCode) return;

    const url = `${window.location.origin}/agentCode/${agentCode}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenCreate = () => {
    setModalState({ isOpen: true, isUpdate: false, agentData: null });
  };

  const handleOpenEdit = (app) => {
    setModalState({
      isOpen: true,
      isUpdate: true,
      agentData: {
        id: app._id || app.id,
        councilName: app.councilName,
        agentId: app.agentId,
        aliasId: app.aliasId,
        siteLink: app.siteLink,
        chatbotType: app.type,
        siteLogo: app.logoUrl || app.iconLogoUrl || null,
      },
    });
  };

  const handleCloseModal = () => {
    setModalState({ isOpen: false, isUpdate: false, agentData: null });
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['chatbotsList'] });
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <EmptyState>
          <p>Loading chatbots listing…</p>
        </EmptyState>
      );
    }

    if (isError) {
      return (
        <EmptyState>
          <p>Failed to load chatbots list</p>
          <p>Please try again later</p>
        </EmptyState>
      );
    }

    if (applications.length === 0) {
      return (
        <EmptyState>
          <p>No chatbots found</p>
        </EmptyState>
      );
    }

    return (
      <ApplicationsGrid>
        {applications.map((app) => {
          const key = app._id || app.id || app.agentCode;

          return (
            <ApplicationCard key={key}>
              <ApplicationIcon>
                {app.logoUrl ? (
                  <img
                    src={app.logoUrl || app.iconLogoUrl}
                    alt={app.councilName}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = app.iconLogoUrl;
                    }}
                  />
                ) : (
                  <File />
                )}
              </ApplicationIcon>

              <ApplicationHeader>
                <ApplicationTitle>{app.councilName}</ApplicationTitle>

                {app.siteLink && (
                  <ApplicationMeta>
                    <ExternalLink size={13} />
                    <a
                      href={app.siteLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      {app.siteLink}
                    </a>
                  </ApplicationMeta>
                )}

                <ApplicationMeta onClick={() => handleInitiateChatBot(app.agentCode)}>
                  <Hash size={13} />
                  <span>{app.agentCode}</span>
                </ApplicationMeta>
              </ApplicationHeader>

              <ApplicationActions>
                <NewApplicationButton type="button" onClick={() => handleView(app)}>
                  View Invoices
                </NewApplicationButton>
                <ViewButton type="button" onClick={() => handleOpenEdit(app)}>
                  Edit
                </ViewButton>
              </ApplicationActions>
            </ApplicationCard>
          );
        })}
      </ApplicationsGrid>
    );
  };

  return (
    <DashboardWrapper>
      <DashboardContainer>
        <HeaderSection>
          <SectionHeader>
            <SectionTitle>Chatbot List</SectionTitle>
            <NewApplicationButton type="button" onClick={handleOpenCreate}>
              <PlusIcon />
              Create Chatbot
            </NewApplicationButton>
          </SectionHeader>
        </HeaderSection>

        {renderContent()}
      </DashboardContainer>

      <CreateAndUpdateCouncilAgent
        isOpen={modalState.isOpen}
        isUpdate={modalState.isUpdate}
        initialData={modalState.agentData}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
      />
    </DashboardWrapper>
  );
};

export default Dashboard;
